/**
 * `<caos-extract-review>` — what was pulled out of a document, least certain first, and what could not be.
 * (Catalog: AI capabilities › ExtractReview — the Extract capability's binding: a typed field patch sorted by confidence, beside the explicit list of what would not map.)
 *
 * CAOS-158. Extract turns a document into a typed field patch, and `ai-platform.md` states the two
 * rules that decide whether the result is reviewable: values that cannot be mapped are returned in
 * an explicit `unmapped` list with their source span and are **never coerced** into the nearest
 * legal value, and per-field confidence rides along "so the review surface can sort the uncertain to
 * the top". This is that review surface, and both rules are structural here rather than advisory.
 *
 * ── THE UNMAPPED LIST IS A SECTION, NOT A FOOTNOTE ──────────────────────────────────────────────
 * The failure this guards against is the one that looks like success. A document names a value the
 * target field cannot hold — a picklist word that is not one of the field's active values, a
 * currency with more decimals than the column declares, a lookup to a record this person may not
 * read — and the tempting behaviour is to round it to the nearest legal thing and show a complete
 * patch. The reviewer then approves a record that says something the document did not.
 *
 * So the unmapped entries are drawn in their own headed section with their source text beside them,
 * and the count is in the summary line a reader cannot miss. A patch where everything mapped says
 * so; a patch where three things did not says THAT, in the same place, in the same words.
 *
 * ── SORTED LEAST CERTAIN FIRST, AND WHY A MISSING CONFIDENCE IS NOT ZERO ────────────────────────
 * Rows order by confidence ascending, because the reader's job is to find what needs looking at and
 * a list ordered by field name buries the one doubtful value among twenty obvious ones. A field with
 * NO confidence reported is not scored zero — that would pin it to the top for ever while nothing
 * was wrong, the same error `<caos-coverage-list>` records for an artifact with no executable lines.
 * Unscored rows sort after the scored ones and read "not scored" rather than "0%".
 *
 * ── IT DOES NOT ACCEPT OR DISCARD ───────────────────────────────────────────────────────────────
 * Deliberately. The architecture says acceptance is an ordinary save by the accepting human and that
 * review is the platform's existing diff surface — field-level before → after, the same rendering
 * data history uses. Putting an Accept button here would be a second, lesser review surface for the
 * same proposal, and the lesser one is the one that ships. This piece answers "is this extraction
 * worth putting through review at all", and {@link blocking} is what a surface gates on.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────────────────────────
 * It does not invoke the capability, write the patch, or open the document. Pressing a source span
 * raises `caos-extract-open-source`; the application scrolls the document wherever the document is.
 * It also does not re-check a value against the field's type: that check belongs to the contract
 * that produced the patch, and a second copy here would disagree with the deploy gate eventually.
 *
 * Styling seams: `::part(review)`, `::part(summary)`, `::part(section)`, `::part(section-heading)`,
 * `::part(row)`, `::part(field)`, `::part(value)`, `::part(confidence)`, `::part(unmapped-row)`,
 * `::part(source)`, `::part(reason)`, `::part(empty)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** One value that could not be mapped onto its target field. Never coerced, always listed. */
export interface CaosExtractUnmapped {
  /** The field it was headed for. Absent ⇒ the document said something no field claims. */
  readonly field?: string;
  /** The text in the document it came from — the source span, as it reads. */
  readonly source: string;
  /** Why it would not map, in the words a reviewer needs: what the field would not take. */
  readonly reason: string;
  /** Where in the document the span starts, when the extractor reported it. */
  readonly at?: number;
}

/**
 * A typed field patch, in the shape the shipped `ai_extract` prompt declares as its output
 * contract (`kernel/src/ai/standard-capabilities.ts`). Written out rather than imported because
 * `@caos/ui` does not depend on the kernel package; keep the members identical to that schema.
 */
export interface CaosExtractPatch {
  /** The object the patch writes to. */
  readonly object: string;
  /** The record it writes to. Absent ⇒ a new record. */
  readonly recordId?: string;
  /** The values that mapped, keyed by field. */
  readonly values: Readonly<Record<string, string | number | boolean | null>>;
  /** Per-field confidence, 0–1. A field absent from here is UNSCORED, which is not zero. */
  readonly confidence?: Readonly<Record<string, number>>;
  /** What would not map. Absent ⇒ the extractor reported nothing, which is not the same as none. */
  readonly unmapped?: readonly CaosExtractUnmapped[];
}

/** One row of the mapped half, after ordering. */
export interface CaosExtractRow {
  readonly field: string;
  readonly value: string | number | boolean | null;
  /** The reported confidence, or `null` when none was reported. */
  readonly confidence: number | null;
}

const sheet = makeSheet(`
  :host { display: block; }

  .review { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
  .summary.blocked { color: var(--caos-color-warning-text); font-weight: 600; }

  .section { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  .section-heading {
    margin: 0;
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }

  .row {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    padding: 6px var(--caos-space-2);
    font-family: var(--caos-font);
    font-size: 13px;
  }

  .field {
    flex: none;
    width: 34%;
    min-width: 0;
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-text-muted);
    overflow-wrap: anywhere;
  }

  .value { flex: 1 1 auto; min-width: 0; color: var(--caos-color-text); overflow-wrap: anywhere; }
  .value.absent { color: var(--caos-color-text-muted); font-style: italic; }

  .confidence {
    flex: none;
    font-family: var(--caos-font-mono);
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
  }
  .confidence.low { color: var(--caos-color-warning-text); font-weight: 600; }
  .confidence.unscored { font-style: italic; }

  .unmapped-row {
    border-color: color-mix(in srgb, var(--caos-color-warning) 45%, transparent);
    background: var(--caos-color-warning-bg);
    flex-wrap: wrap;
  }

  .source {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    padding: 0;
    background: none;
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-warning-text);
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    overflow-wrap: anywhere;
  }
  .source:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }
  .source.flat { text-decoration: none; cursor: default; }

  .reason {
    flex: 1 1 100%;
    font-size: 12px;
    color: var(--caos-color-warning-text-muted);
  }

  .empty {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

/** Below this, a value is called out rather than merely numbered. */
const LOW_CONFIDENCE = 0.6;

export class CaosExtractReview extends CaosElement {
  static get observedAttributes(): string[] {
    return ['empty-text', 'mapped-heading', 'unmapped-heading'];
  }

  private summaryEl: HTMLParagraphElement;
  private mappedEl: HTMLDivElement;
  private mappedHeadEl: HTMLParagraphElement;
  private mappedRowsEl: HTMLDivElement;
  private unmappedEl: HTMLDivElement;
  private unmappedHeadEl: HTMLParagraphElement;
  private unmappedRowsEl: HTMLDivElement;
  private emptyEl: HTMLParagraphElement;

  private current: CaosExtractPatch | null = null;

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'review';
    wrap.setAttribute('part', 'review');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    [this.mappedEl, this.mappedHeadEl, this.mappedRowsEl] = this.section();
    [this.unmappedEl, this.unmappedHeadEl, this.unmappedRowsEl] = this.section();

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.setAttribute('part', 'empty');

    wrap.append(this.summaryEl, this.mappedEl, this.unmappedEl, this.emptyEl);
    this.root.append(wrap);
  }

  private section(): [HTMLDivElement, HTMLParagraphElement, HTMLDivElement] {
    const box = document.createElement('div');
    box.className = 'section';
    box.setAttribute('part', 'section');
    const heading = document.createElement('p');
    heading.className = 'section-heading';
    heading.setAttribute('part', 'section-heading');
    const rows = document.createElement('div');
    rows.className = 'section';
    box.append(heading, rows);
    return [box, heading, rows];
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get patch(): CaosExtractPatch | null {
    return this.current;
  }

  set patch(next: CaosExtractPatch | null) {
    this.current =
      next !== null && typeof next === 'object' && typeof next.object === 'string' && next.values !== null && typeof next.values === 'object'
        ? next
        : null;
    this.render();
  }

  /**
   * The mapped fields, least certain first.
   *
   * Scored rows ascend by confidence; unscored rows follow, alphabetical, because "not scored" is a
   * different statement from "scored low" and sorting them together would make the extractor's
   * silence look like its doubt.
   */
  get rows(): readonly CaosExtractRow[] {
    const patch = this.current;
    if (patch === null) return [];
    const confidence = patch.confidence ?? {};
    const rows: CaosExtractRow[] = Object.keys(patch.values).map((field) => {
      const score = confidence[field];
      return {
        field,
        value: patch.values[field] ?? null,
        confidence: typeof score === 'number' && Number.isFinite(score) ? score : null,
      };
    });
    return rows.sort((a, b) => {
      if (a.confidence === null && b.confidence === null) return a.field.localeCompare(b.field);
      if (a.confidence === null) return 1;
      if (b.confidence === null) return -1;
      return a.confidence - b.confidence || a.field.localeCompare(b.field);
    });
  }

  /** What would not map. Empty when the extractor reported none — and when it reported nothing. */
  get unmapped(): readonly CaosExtractUnmapped[] {
    const list = this.current?.unmapped;
    return (Array.isArray(list) ? list : []).filter(
      (u): u is CaosExtractUnmapped => u !== null && typeof u === 'object' && typeof u.source === 'string' && typeof u.reason === 'string',
    );
  }

  /**
   * Whether this extraction should be looked at before it goes anywhere.
   *
   * True when anything would not map, or when any mapped value is below the low-confidence mark.
   * Exposed so a surface can GATE on it rather than only tint a row — which is the difference
   * between a review surface and a decorated one.
   *
   * It counts the RAW entries, not the drawable ones. {@link unmapped} drops an entry missing the
   * source or the reason, because there is nothing useful to draw for it — but an entry that is
   * there at all is still the extractor saying something did not map, and letting a malformed one
   * turn the gate OFF would be the failure this whole component is about: a patch that looks clean
   * because the evidence against it was unreadable. The kernel contract requires both fields, so
   * this only arises off the contract path; a gate that depends on somebody else having validated
   * first is not a gate.
   */
  get blocking(): boolean {
    const raw = this.current?.unmapped;
    const declared = Array.isArray(raw) ? raw.length : 0;
    return declared > 0 || this.rows.some((r) => r.confidence !== null && r.confidence < LOW_CONFIDENCE);
  }

  private render(): void {
    const rows = this.rows;
    const unmapped = this.unmapped;
    const nothing = this.current === null || (rows.length === 0 && unmapped.length === 0);

    this.emptyEl.hidden = !nothing;
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'Nothing was extracted from this document.';
    this.summaryEl.hidden = nothing;
    this.mappedEl.hidden = nothing || rows.length === 0;
    this.unmappedEl.hidden = nothing || unmapped.length === 0;
    if (nothing) {
      this.mappedRowsEl.replaceChildren();
      this.unmappedRowsEl.replaceChildren();
      return;
    }

    this.summaryEl.classList.toggle('blocked', this.blocking);
    this.summaryEl.textContent = summarise(rows.length, unmapped.length, rows.filter((r) => r.confidence !== null && r.confidence < LOW_CONFIDENCE).length);

    this.mappedHeadEl.textContent = this.getAttribute('mapped-heading') ?? 'Fields this would set';
    this.mappedRowsEl.replaceChildren(...rows.map((row) => this.mappedRow(row)));

    this.unmappedHeadEl.textContent = this.getAttribute('unmapped-heading') ?? 'Would not map — left for a person';
    this.unmappedRowsEl.replaceChildren(...unmapped.map((entry) => this.unmappedRow(entry)));
  }

  private mappedRow(row: CaosExtractRow): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'row';
    el.setAttribute('part', 'row');

    const field = document.createElement('span');
    field.className = 'field';
    field.setAttribute('part', 'field');
    field.textContent = row.field;

    const value = document.createElement('span');
    value.className = 'value';
    value.setAttribute('part', 'value');
    // A null in a patch is an instruction to CLEAR the field, which is a change and not an absence.
    // Printing it as an empty cell would make "set this to nothing" indistinguishable from "this row
    // failed to draw", and the reviewer would approve a clearance they never saw.
    if (row.value === null) {
      value.classList.add('absent');
      value.textContent = 'cleared';
    } else {
      value.textContent = String(row.value);
    }

    const score = document.createElement('span');
    score.className = 'confidence';
    score.setAttribute('part', 'confidence');
    if (row.confidence === null) {
      score.classList.add('unscored');
      score.textContent = 'not scored';
    } else {
      if (row.confidence < LOW_CONFIDENCE) score.classList.add('low');
      score.textContent = `${Math.round(row.confidence * 100)}%`;
    }

    el.setAttribute(
      'aria-label',
      `${row.field}: ${row.value === null ? 'cleared' : String(row.value)}, ${
        row.confidence === null ? 'confidence not scored' : `confidence ${Math.round(row.confidence * 100)} percent`
      }`,
    );
    el.append(field, value, score);
    return el;
  }

  private unmappedRow(entry: CaosExtractUnmapped): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'row unmapped-row';
    el.setAttribute('part', 'row unmapped-row');

    const field = document.createElement('span');
    field.className = 'field';
    field.setAttribute('part', 'field');
    // An entry with no field is the document saying something no field claims, which is a real and
    // different finding from "this field refused this value" — so it reads as itself.
    field.textContent = entry.field ?? 'no field claims this';

    const source = document.createElement('button');
    source.type = 'button';
    source.className = 'source';
    source.setAttribute('part', 'source');
    source.textContent = entry.source;
    if (entry.at === undefined) {
      // Nothing to scroll to. The control is flat and inert rather than absent: the span still has
      // to be readable, and a button that looks live and does nothing is worse than a line of text.
      source.classList.add('flat');
      source.disabled = true;
    } else {
      source.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('caos-extract-open-source', {
            bubbles: true,
            composed: true,
            detail: { field: entry.field ?? null, source: entry.source, at: entry.at ?? null },
          }),
        );
      });
    }

    const reason = document.createElement('span');
    reason.className = 'reason';
    reason.setAttribute('part', 'reason');
    reason.textContent = entry.reason;

    el.setAttribute('aria-label', `Would not map${entry.field === undefined ? '' : ` onto ${entry.field}`}: ${entry.source}. ${entry.reason}`);
    el.append(field, source, reason);
    return el;
  }
}

/** The one line above both sections. Says the count that decides whether to read on. */
function summarise(mapped: number, unmapped: number, low: number): string {
  const head = `${mapped} ${mapped === 1 ? 'field' : 'fields'} extracted`;
  const tail: string[] = [];
  if (unmapped > 0) tail.push(`${unmapped} ${unmapped === 1 ? 'value' : 'values'} would not map`);
  if (low > 0) tail.push(`${low} below ${Math.round(LOW_CONFIDENCE * 100)}% confidence`);
  return tail.length === 0 ? `${head}, all mapped and none in doubt.` : `${head}; ${tail.join(', ')}.`;
}

define('caos-extract-review', CaosExtractReview);

declare global {
  interface HTMLElementTagNameMap {
    'caos-extract-review': CaosExtractReview;
  }
}
